import { useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';

interface DirectoryRow {
  id: string;
  name: string;
}

interface DirectoryApi<I, V> {
  create: (input: I) => Promise<V>;
  update: (id: string, input: I) => Promise<V>;
  deactivate: (id: string) => Promise<void>;
  reactivate: (id: string) => Promise<void>;
}

/** Create, edit, deactivate and reactivate for a reference-data directory (garages, suppliers): a form failure stays inline, row actions toast. */
export function useDirectoryMutations<I extends { name: string }, V extends DirectoryRow>(api: DirectoryApi<I, V>, reload: () => Promise<void>) {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function save(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch (err) {
      setSaveError(apiErrorMessage(err) ?? 'Could not save — check the fields and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  return {
    saveError,
    saving,
    resetSaveError: () => setSaveError(null),
    create: (input: I) => save(() => api.create(input), `${input.name} created`),
    update: (id: string, input: I) => save(() => api.update(id, input), `${input.name} updated`),
    deactivate: (row: V) => rowAction(() => api.deactivate(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`),
    reactivate: (row: V) => rowAction(() => api.reactivate(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`),
  };
}
